import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from "react";

import type { DemoAdjustment, EdgeMoveInput } from "@/api";
import { Button } from "@/components/Button";
import { Panel } from "@/components/Panel";
import { StateMessage } from "@/components/StateMessage";
import { StatusBadge } from "@/components/StatusBadge";
import { formatMm } from "@/lib/format";
import {
  BOARD_FALLBACK_WIDTH_PX,
  drawingBoard,
  labelBox,
  placeLabels,
  type LabelKind,
  type LabelPlacement,
  type LabelRequest,
} from "@/lib/labels";
import {
  closureGap,
  closureText,
  dimensionMark,
  isContour,
  isPartial,
  isSegmentRead,
  outlineProjection,
  outlineSegments,
  type DemoFinding,
  type DemoOutline,
  type DemoSegment,
  type EdgeState,
  type ScreenPoint,
} from "@/lib/outline";
import { gratingHatch, type ProjectionOptions } from "@/lib/projection";

import { OutlinePicker } from "./OutlinePicker";
import styles from "./OutlineReview.module.css";

/** Stroke/fill per wall state — the data palette (CI §2.1), never brand red. */
const STATE_COLOUR: Record<EdgeState, string> = {
  grating: "var(--st-auto)",
  grating_unknown: "var(--st-review)",
  no_grating: "var(--ink-3)",
  undimensioned: "var(--ink-3)",
  interruption: "var(--ink-2)",
  form_only: "var(--ink-2)",
};

/**
 * The word for each state, in the order the legend lists them. Colour alone
 * must not carry the difference between "Rost" and "Rost unklar" — a
 * red-green weak reader gets the same information a sighted one does only
 * because the word is printed, not implied (CI §7 Prüffrage 4).
 */
const STATE_LABEL: Record<EdgeState, string> = {
  grating: "Rost",
  grating_unknown: "Rost unklar — nicht bestätigt",
  no_grating: "kein Rost",
  undimensioned: "kein Maß",
  interruption: "Unterbrechung",
  // Unreachable in practice: a contour is legended by `CONTOUR_LEGEND` below
  // instead of this per-state loop (spec §7 — no Rost, no Teilzüge on this
  // screen), and `form_only` is the contour's own state (module header of
  // `outline.ts`'s `EdgeState`). Kept only so the table stays exhaustive over
  // `EdgeState` and says something sane if that ever changes.
  form_only: "durchgezogen: gelesenes Maß",
};

/**
 * The two state-driven rows of a contour's legend, in place of the per-state
 * loop below — spec §7 names three states in total, and these are the two
 * that map onto a `segment.state`; the third, "auslaufend gestrichelt", is
 * not a state at all but the same wall's dash pattern changing partway along
 * its own length (`openEnd`, independent of whether that wall was read), so
 * it is rendered separately below with its own swatch and its own gate —
 * only where a wall is actually drawn open-ended, unlike these two, which
 * gate on `present.length > 0` (some contour wall exists at all).
 *
 * Reuses `form_only`'s and `undimensioned`'s own swatches — solid ink-2,
 * dashed ink-3 — because that is still the drawn distinction underneath
 * (`Wall`'s `contour` branch below), only named for what it now means rather
 * than for the grating state it used to.
 */
type ContourLegendState = Exclude<
  EdgeState,
  "grating" | "grating_unknown" | "no_grating" | "interruption"
>;

/**
 * Named for where the figures came from, because that is the one word on this
 * board a person could be misled by: „gelesen" is a claim that a model read
 * something, and on a hand-drawn outline nothing was read (design 2026-08-15).
 * Passed in rather than sniffed off the segments — an outline somebody drew
 * and put no figure on yet carries nothing to sniff.
 */
function contourLegend(
  figuresAre: FiguresOrigin,
): { state: ContourLegendState; text: string }[] {
  const set = figuresAre === "set";
  return [
    { state: "form_only", text: set ? "durchgezogen: gesetztes Maß" : "durchgezogen: gelesenes Maß" },
    {
      state: "undimensioned",
      text: set ? "gestrichelt: kein Maß gesetzt" : "gestrichelt: kein Maß gelesen",
    },
  ];
}

/** Whether the figures on this outline were read off a sheet or typed by hand. */
export type FiguresOrigin = "read" | "set";

/**
 * What the pane is showing. An empty `drawingUrl` means there never was a
 * sheet — the outline was drawn by hand — and promising „daneben das Original"
 * beside an empty half of the board is a sentence about a document nobody
 * handed over.
 */
function paneHint(drawingUrl: string, pending: boolean): string {
  if (pending) return "die Skizze in maßstabsgetreu, daneben das Original — wird geladen";
  return drawingUrl
    ? "die Skizze in maßstabsgetreu, daneben das Original"
    : "maßstabsgetreu aus deiner Zeichnung — kein Blatt dahinter";
}

/** The legend's own word for `openEnd` (spec §7's third state, "auslaufend
 * gestrichelt") — kept beside `CONTOUR_LEGEND` rather than folded into it,
 * since it needs `OpenEndLegendSwatch` instead of `LegendSwatch`. */
const OPEN_END_LEGEND_TEXT = "auslaufend gestrichelt: Zeichnung bricht hier ab";

const STATE_ORDER: EdgeState[] = [
  "grating",
  "grating_unknown",
  "no_grating",
  "form_only",
  "undimensioned",
  "interruption",
];

const HATCH_SPACING = 9;
const HATCH_HALF_WIDTH = 4;

/** How far the dimension arrow sits off its wall, in CSS px — the same room
 * `.svg`'s padding already reserves for a workshop-style dimension line
 * (`useBoard`/`drawingBoard`). */
const DIMENSION_OFFSET_PX = 20;

/**
 * The dimension offset, signed so the arrow lands outside the shape rather
 * than through it — the same outward convention `dimensionLine`
 * (`lib/projection.ts`) and `frameOf` (`lib/labels.ts`) already use: flip the
 * offset whenever the wall's own midpoint sits on the near side of the
 * centroid, so a positive `dimensionMark` offset always ends up on the far
 * side, away from the house.
 *
 * `dimensionMark` itself does not take a centroid — it only knows one wall,
 * not the shape it belongs to — so this is the caller's half of the
 * decision, kept beside the other callers of the same convention instead of
 * duplicated a third time in `lib/outline.ts`.
 */
function outwardOffsetPx(
  from: ScreenPoint,
  to: ScreenPoint,
  centroid: ScreenPoint,
  magnitude: number,
): number {
  const horizontal = Math.abs(to.x - from.x) >= Math.abs(to.y - from.y);
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  const near = horizontal ? midY < centroid.y : midX < centroid.x;
  return near ? -magnitude : magnitude;
}

/**
 * The step every correction is rounded to, in millimetres — and the step one
 * arrow key press is worth.
 *
 * A drag is a *request*, not a measurement: the binding geometry comes back
 * from `apply_edits` in the backend and is drawn from the next answer, never
 * computed here. What the pointer contributes is a distance in pixels, and at
 * the scale a facade is drawn at one pixel is tens of millimetres — so the
 * figure it produces is rounded to something a person would have typed. It
 * also settles the abort: a gesture that rounds to nothing was a click on a
 * wall, and a click must not change the house.
 */
const EDIT_STEP_MM = 10;

/** Half the width of the invisible band a wall can be grabbed by, in CSS px. */
const GRAB_HALF_WIDTH = 7;

/**
 * The stable "nothing" for the three list-shaped props below.
 *
 * A `= []` default in the parameter list builds a fresh array on every render,
 * which is a new prop identity for every child that takes one and a changed
 * dependency for every `useMemo` that watches one. One frozen empty array
 * says the same thing and stays the same thing.
 */
const NO_EDITS: readonly EdgeMoveInput[] = Object.freeze([]);
const NO_ADJUSTMENTS: readonly DemoAdjustment[] = Object.freeze([]);
const NO_CLAMPS: readonly number[] = Object.freeze([]);

/** Which way a wall may be pushed: `"y"` for a horizontal wall, `"x"` for a vertical one. */
type DragAxis = "x" | "y";

/**
 * The axis a wall travels along, read off the two points it is drawn between.
 *
 * Mirrors `schema/outline_edit.py`'s `_axis` — a horizontal wall (shared y)
 * moves in y, a vertical one (shared x) moves in x — and takes the same
 * convention for the sign: `projectRun` keeps the backend's orientation, so a
 * pixel further down the screen is a larger `yMm` and a pixel to the right a
 * larger `xMm`. That is the whole reason a drag can be reported as a plain
 * `deltaMm` without a normal vector: the screen axis and the world axis are
 * the same axis, pointing the same way.
 */
function dragAxis(from: ScreenPoint, to: ScreenPoint): DragAxis {
  return Math.abs(to.y - from.y) > Math.abs(to.x - from.x) ? "x" : "y";
}

/**
 * What one arrow key press is worth on a wall that moves along `axis` — or
 * `null` where that key does not move this wall at all.
 *
 * Two keys per wall, not four. A wall that only travels up and down is not
 * moved sideways by any amount, and a key that silently did nothing on half
 * the walls would be worse than one that does nothing everywhere: the same
 * gesture would sometimes change the house and sometimes not.
 */
function arrowStep(key: string, axis: DragAxis): number | null {
  if (axis === "y") {
    if (key === "ArrowDown") return EDIT_STEP_MM;
    if (key === "ArrowUp") return -EDIT_STEP_MM;
    return null;
  }
  if (key === "ArrowRight") return EDIT_STEP_MM;
  if (key === "ArrowLeft") return -EDIT_STEP_MM;
  return null;
}

/**
 * The segment index either side of `index`, wrapping the ring seam when the
 * outline is closed — the ends of an open chain simply have one neighbour.
 *
 * A transcription of `schema/outline_edit.py`'s `_neighbours`, and it has to
 * stay one: it is read here for exactly the question that module answers with
 * an exception, so a different notion of "neighbour" would offer a grip the
 * backend then refuses. The seam is the part worth spelling out — a closed
 * candidate's first and last segments are as adjacent as any other pair, and
 * a check that treated the ends of the list as ends of the shape would get
 * both of them wrong.
 */
function neighbourIndices(index: number, count: number, closed: boolean): number[] {
  const wrap = closed && count > 1;
  const left = index > 0 ? index - 1 : wrap ? count - 1 : null;
  const right = index + 1 < count ? index + 1 : wrap ? 0 : null;
  return [left, right].filter((neighbour): neighbour is number => neighbour !== null);
}

/**
 * The walls of this outline that can actually be moved.
 *
 * A wall travels perpendicular to itself and its neighbours absorb the move
 * as a length change. A neighbour running the **same** way has no length to
 * give, and `apply_edits` refuses that drag outright rather than deforming
 * the shape (`edit_not_draggable`, 422). A straight joint is not exotic:
 * three of A-1's real candidates are chains of them, and ST-3's own reading
 * has three `turn: straight` segments in a row — so without this, most of the
 * walls in a demonstration would carry a handle that could only ever fail.
 * A control that always answers 422 is worse than no control (fix round 1,
 * controller decision, 11.08.2026).
 *
 * This is not the frontend deciding a rule of its own. The axis of a wall is
 * already computed here for the cursor and the arrow keys, and the rule read
 * off it is the backend's, transcribed beside `neighbourIndices`. Where the
 * two ever disagree the backend still wins: it refuses, the screen names the
 * refusal and the retry drops the correction.
 *
 * A lone segment has no neighbour at all and stays movable — nothing has to
 * absorb anything, the whole wall simply travels, which `apply_edits` allows.
 */
function movableWalls(axes: DragAxis[], closed: boolean): Set<number> {
  const movable = new Set<number>();
  axes.forEach((axis, index) => {
    const parallel = neighbourIndices(index, axes.length, closed).some(
      (neighbour) => axes[neighbour] === axis,
    );
    if (!parallel) movable.add(index);
  });
  return movable;
}

/**
 * How the reconstruction is currently turned relative to the orientation the
 * backend delivered it in — display only (decision Denis, 12.08.2026).
 *
 * The extraction reports relative turns only (`start`/`left`/`right`/
 * `straight`), never an absolute orientation, so `reconstruct` always starts
 * at (0,0) heading +x and "right" always becomes "down" on screen. For any
 * run there are 8 equally valid depictions (4 rotations × 2 mirrorings) and
 * the backend draws exactly one, arbitrarily — this is the state a person
 * uses to turn it to match the sketch beside it.
 *
 * `flipX`/`flipY` are independent flags rather than one "mirrored" boolean
 * combined with `rotation`: two flags that each negate their own axis need
 * no algebra to reason about, whereas folding a vertical mirror into a
 * horizontal one plus a half turn would make every reader re-derive that
 * identity before trusting a line of this file. The two encodings can reach
 * the same picture two different ways (`flipX && flipY` looks exactly like
 * `rotation` turned by 180° with neither flag set) and that overlap is
 * harmless: nothing ever compares two `ViewState`s for equality, only
 * `applyView`/`applyViewVector` ever read one, and both produce one
 * definite, correct picture for whatever combination they are given.
 */
type ViewRotation = 0 | 90 | 180 | 270;

interface ViewState {
  /** Degrees clockwise, applied after the two flips below. */
  rotation: ViewRotation;
  /** Mirrored left-right. */
  flipX: boolean;
  /** Mirrored top-to-bottom. */
  flipY: boolean;
}

/** The orientation the backend delivered — no turn, no mirror. */
const VIEW_IDENTITY: ViewState = { rotation: 0, flipX: false, flipY: false };

/** In rotation order, so the two turn buttons can step through it by index
 * instead of doing modular arithmetic on a union type TypeScript cannot
 * itself prove stays inside `ViewRotation`. */
const VIEW_ROTATIONS: readonly ViewRotation[] = [0, 90, 180, 270];

function isViewIdentity(view: ViewState): boolean {
  return view.rotation === 0 && !view.flipX && !view.flipY;
}

/**
 * One 90° clockwise turn about the origin — SVG's own `rotate(90)`, which is
 * `(x, y) → (−y, x)` in a y-down coordinate system (screen right becomes
 * screen down, matching "90° nach rechts"). Composing this primitive four
 * times is the identity and needs no trigonometry, which is the point: a
 * `Math.cos(Math.PI / 2)` is 6·10⁻¹⁷, not exactly zero, and a rotation this
 * screen exists to keep exact — a wall's drawn length must not drift by a
 * rounding error nobody asked for.
 */
function quarterTurnClockwise(vector: ScreenPoint): ScreenPoint {
  return { x: -vector.y, y: vector.x };
}

/** `vector`, turned clockwise by `turns` quarter turns — negative turns
 * counter-clockwise, and any integer works, because the loop below first
 * folds it into 0-3. */
function rotateQuarters(vector: ScreenPoint, turns: number): ScreenPoint {
  let result = vector;
  const steps = ((turns % 4) + 4) % 4;
  for (let i = 0; i < steps; i += 1) result = quarterTurnClockwise(result);
  return result;
}

/**
 * `vector` under the view's flips and rotation, in that order. A direction
 * rather than a position — no centre to rotate about, which is exactly why
 * this is the half of the transform the drag math below reuses: a pointer's
 * *travel* has no absolute position to preserve, only a direction to carry
 * through the same turn the drawing itself was given.
 */
function applyViewVector(vector: ScreenPoint, view: ViewState): ScreenPoint {
  const flipped = {
    x: view.flipX ? -vector.x : vector.x,
    y: view.flipY ? -vector.y : vector.y,
  };
  return rotateQuarters(flipped, view.rotation / 90);
}

/**
 * The forward transform's exact inverse: undo the rotation, then the flips —
 * the reverse of the order `applyViewVector` applies them in, which is what
 * makes it an inverse rather than just another point of the same group.
 * Flipping twice is the identity and rotating by `-turns` undoes rotating by
 * `turns`, so `invertViewVector(applyViewVector(v, view), view)` is `v`
 * exactly, not approximately — every step here is a sign flip or a
 * coordinate swap, never a multiplication by `sin`/`cos`.
 *
 * This is the mapping decision 3 asks for: a pointer's raw screen travel,
 * carried back through whatever the view currently does, into the same
 * local space `dragAxis`/`toMm` already speak — so a 90° turn changes what a
 * gesture *looks like* without changing what it *means* to `apply_edits`.
 */
function invertViewVector(vector: ScreenPoint, view: ViewState): ScreenPoint {
  const unrotated = rotateQuarters(vector, -(view.rotation / 90));
  return {
    x: view.flipX ? -unrotated.x : unrotated.x,
    y: view.flipY ? -unrotated.y : unrotated.y,
  };
}

/** `point`, carried through the view's flips and rotation about `center` —
 * the position analogue of {@link applyViewVector}, for the geometry itself
 * rather than a pointer's travel across it. */
function applyView(point: ScreenPoint, view: ViewState, center: ScreenPoint): ScreenPoint {
  const local = applyViewVector({ x: point.x - center.x, y: point.y - center.y }, view);
  return { x: local.x + center.x, y: local.y + center.y };
}

function rotateViewRight(view: ViewState): ViewState {
  const index = VIEW_ROTATIONS.indexOf(view.rotation);
  return { ...view, rotation: VIEW_ROTATIONS[(index + 1) % 4]! };
}

function rotateViewLeft(view: ViewState): ViewState {
  const index = VIEW_ROTATIONS.indexOf(view.rotation);
  return { ...view, rotation: VIEW_ROTATIONS[(index + 3) % 4]! };
}

function flipViewHorizontal(view: ViewState): ViewState {
  return { ...view, flipX: !view.flipX };
}

function flipViewVertical(view: ViewState): ViewState {
  return { ...view, flipY: !view.flipY };
}

/**
 * The view, as a plain string on the drawing itself — so a test can read
 * what is currently turned without decoding it back out of transformed
 * coordinates, and so nothing here has to invent a second notion of "the
 * current view" that could drift from the one driving the render.
 */
function viewDataAttribute(view: ViewState): string {
  return `${view.rotation}|${view.flipX}|${view.flipY}`;
}

/** `+600 mm` / `−600 mm` — the sign spelled out, so a "−" cannot go missing. */
function signedMm(valueMm: number): string {
  return `${valueMm < 0 ? "−" : "+"}${formatMm(Math.abs(valueMm))}`;
}

/**
 * How each sort of text on the board is set. One entry per {@link LabelKind},
 * so the placement pass in `lib/labels.ts` and the glyphs here cannot drift
 * apart: that pass reserves a box per kind, this table paints into it.
 *
 * The colours are the data palette, never brand red (CI §2.1). A measurement
 * takes its fill from the stylesheet, because it is not a state — it is a
 * number, and it reads in `--ink-2` whatever the wall under it says.
 */
const LABEL_STYLE: Record<LabelKind, { className?: string; fill?: string }> = {
  dimension: { className: styles.dimensionLabel },
  interruption: { className: styles.interruptionLabel, fill: STATE_COLOUR.interruption },
  mark: { className: styles.uncertainMark, fill: STATE_COLOUR.grating_unknown },
  landmark: { className: styles.landmarkLabel, fill: STATE_COLOUR.undimensioned },
};

/**
 * Under the disabled confirm button while the result is superseded.
 *
 * States the consequence, not the cause: the cause is the caller's sentence
 * and it is already printed at the picker a few lines above, where the other
 * dead control is. Printing it twice on one card would be the same warning
 * shouted at a reader who has just read it.
 */
const SUPERSEDED_CONFIRM_NOTE = "Bestätigt würde die Form eines Laufs, der nicht mehr gilt.";

interface LineStyle {
  width: number;
  linecap: "square" | "round";
  dash?: string;
  /** Cross bars via `gratingHatch` — the product's own vocabulary, not a highlight. */
  hatch: boolean;
}

/** How the four states with an actual line are drawn. `interruption` has no
 * line at all (a text anchor instead, spec §2.6) and is not in this table. */
const LINE_STYLE: Record<Exclude<EdgeState, "interruption">, LineStyle> = {
  grating: { width: 2, linecap: "square", hatch: true },
  grating_unknown: { width: 2, linecap: "square", hatch: true },
  no_grating: { width: 1.5, linecap: "square", hatch: false },
  undimensioned: { width: 2, linecap: "round", dash: "3 5", hatch: false },
  // No hatch: the cross bars are the product, and this wall states nothing
  // about the product. A plain line, a shade darker than an undimensioned one.
  form_only: { width: 2, linecap: "square", hatch: false },
};

/**
 * The drawing board, in CSS pixels, measured rather than assumed.
 *
 * The board used to be a fixed 520-unit `viewBox` stretched to `width: 100%`.
 * Every length inside it — the 10,5 px figure above all — therefore meant
 * 10,5 px only when the pane happened to be 520 px wide. Just above the
 * two-column breakpoint the pane halves to about 385 px and the figure with
 * it, to 7,8 px; at 360 px viewport width it reads 5,6 px (review finding
 * N-3, 10.08.2026). **The drawing may shrink, the figure may not.**
 *
 * With the `viewBox` measured off the pane, one user unit is one CSS pixel at
 * every width: the type size is the type size, and the boxes `placeLabels`
 * reserves for the text mean what they say. The height stays fixed —
 * a board that kept its aspect ratio would shrink in both directions on a
 * phone, and height is the one thing a phone still has.
 *
 * `ResizeObserver` where it exists, because the pane also changes width
 * without the viewport doing so; the resize listener is the fallback for
 * jsdom, where `clientWidth` is 0 anyway and the whole hook resolves to the
 * fallback board.
 */
function useBoard(): { ref: RefObject<HTMLElement | null>; board: ProjectionOptions } {
  const ref = useRef<HTMLElement | null>(null);
  const [width, setWidth] = useState(BOARD_FALLBACK_WIDTH_PX);
  const board = useMemo(() => drawingBoard(width), [width]);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setWidth(Math.round(element.clientWidth) || BOARD_FALLBACK_WIDTH_PX);
    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, board };
}

/**
 * The rebuilt house next to the sketch it came from.
 *
 * A drawing to scale looks nothing like the sketch, so something has to say it
 * is the same drawing put right. The machine checks order — nothing missing,
 * nothing swapped — and stops there: proportion is not comparable, because the
 * sketch is deliberately not to scale (the 17,5 m wall in A-1 is drawn short).
 * Whether it is this customer's house is a person's answer, and the confirm
 * button below is the only thing in this whole phase that writes anything.
 *
 * **Nothing is chosen until somebody chooses** (review finding C1,
 * 10.08.2026). There is no fallback to the last candidate here: on the first
 * run the backend marks no candidate `chosen` — correctly, A-014 — and a
 * fallback made the unconnected arrangement the *normal* case rather than the
 * emergency the comment above it described. The consequence was a live
 * confirm button under a sentence claiming a reconciliation that, for the
 * separate arrangement, never runs. Until a shape is picked there is nothing
 * to confirm, so there is no button and no verdict, only the one thing that
 * is missing. And the confirmation can be taken back — a misclick that
 * cannot be undone is worse than no button at all.
 *
 * **A superseded result is shown, not operated** (review findings N-1/N-8,
 * 10.08.2026). The screen keeps the last result across a failed run so nobody
 * is left with a blank page — but the shapes in it were computed by a run
 * that is over. Clicking one asks the backend for an id it no longer knows
 * and walks into the same 422; confirming one writes "Bestätigt von …" under
 * a red error card. `superseded` says why the result is not current, switches
 * both controls off and prints that reason where the controls are.
 */
export function OutlineReview({
  outlines,
  findings,
  drawingUrl,
  drawingPending = false,
  onChoose,
  onConfirm,
  onRevoke,
  confirmedBy,
  onContinue,
  continueLabel,
  busy = false,
  superseded = null,
  picker = true,
  figuresAre = "read",
  edits = NO_EDITS,
  onEdits,
  adjustments = NO_ADJUSTMENTS,
  editsClamped = NO_CLAMPS,
}: {
  outlines: DemoOutline[];
  findings: DemoFinding[];
  drawingUrl: string;
  /**
   * Whether the url is still being fetched — not the same statement as an
   * empty one.
   *
   * An empty `drawingUrl` says there never was a sheet and the pane is not
   * drawn at all (design 2026-08-15 §4.3). A stored Vorgang has to look its
   * document up first (`useCase`), and during that second the url is also
   * empty — so without this the Original pane was absent, said "kein Blatt
   * dahinter", and then appeared, which is three different claims about one
   * drawing in a row (fix round 1, item 12).
   */
  drawingPending?: boolean;
  onChoose: (id: string) => void;
  onConfirm: (id: string) => void;
  onRevoke: () => void;
  confirmedBy: string | null;
  /**
   * Where a confirmed shape leads, and what that step is called.
   *
   * Optional because the two hosts of this card end differently: the Schema
   * step goes on to the planning, the Durchstich ends at the confirmation on
   * purpose. Handed in as a callback rather than a route so this component
   * stays ignorant of navigation.
   *
   * Rendered **in the confirmed row**, primary: the header may carry a quiet
   * link, but the person's eye is here, where they just confirmed — a
   * finished step whose only control is „Bestätigung zurücknehmen" is a dead
   * end wearing a green badge (seen in use, 16.08.2026).
   */
  onContinue?: () => void;
  continueLabel?: string;
  busy?: boolean;
  /**
   * Whether this card carries the shape picker itself.
   *
   * The Durchstich's does, because nothing is drawn there until somebody
   * picks: the list of candidates *is* the review's first step. The Schema
   * step draws an unambiguous shape straight away (design 12.08.2026 §3) and
   * folds the other readings away under „Andere Lesarten", so it hosts the
   * picker itself — one picker on the page rather than two, and `onChoose`
   * still leads to the same place.
   */
  picker?: boolean;
  /** Why this result is no longer the current one — `null` while it is. */
  superseded?: string | null;
  /**
   * Every correction the result on screen was computed from, complete and in
   * the order it was made.
   *
   * Owned by the screen above (spec §6.2: the demo process holds no session,
   * so each request carries the whole list). It is handed down rather than
   * kept here because a drag has to report the *complete next* list, and a
   * component that guessed at the previous one would drop whatever it had not
   * seen — a retry's corrections, for instance.
   */
  /**
   * Where the figures on this outline came from. `"set"` on an outline
   * somebody drew, where nothing was read and the legend must not say it was
   * (design 2026-08-15). Defaults to `"read"`, which is every other caller.
   */
  figuresAre?: FiguresOrigin;
  edits?: readonly EdgeMoveInput[];
  /**
   * Where a correction is reported to. Absent means the drawing is read-only:
   * no grab bands, no arrow keys, no reset — nothing that invites a gesture
   * with nowhere to go.
   */
  onEdits?: (edits: EdgeMoveInput[]) => void;
  /** Every wall a correction touched, before and after, from the backend. */
  adjustments?: readonly DemoAdjustment[];
  /** Walls whose correction was reined in at the 1 mm boundary (§6.1). */
  editsClamped?: readonly number[];
}) {
  const { ref, board } = useBoard();

  if (outlines.length === 0) {
    return (
      <Panel title="Umriss" hint={paneHint(drawingUrl, drawingPending)}>
        <StateMessage title="Keine Anordnung gefunden">
          Aus den gelesenen Teilzügen ließ sich keine Anordnung bilden. Ohne Anordnung gibt es
          nichts, das sich mit der Skizze vergleichen und bestätigen ließe.
        </StateMessage>
      </Panel>
    );
  }

  // No fallback. `chosen` is null until the backend reports a candidate a
  // person asked for, and every state below reads that null as "nobody has
  // chosen yet" rather than quietly standing in for it.
  const chosen = outlines.find((shape) => shape.chosen) ?? null;

  /**
   * Whether a wall of this drawing may be moved at all — one condition, so
   * there is one place to read it and one place to break it.
   *
   * `connected` is stated here rather than left to the pane below, which only
   * draws a connected shape anyway: the two rules coincide today but are not
   * the same rule. One is about what can be *drawn* (a set of separate bars
   * claims no shape, E-14/E-15); this one is about what can be *corrected*,
   * and the backend refuses an edit without a chosen connected arrangement in
   * its own words (`outline_required`).
   *
   * `busy` and `superseded` join it for the reason the picker and the confirm
   * button already carry them (review findings N-1/N-8): a correction is
   * addressed to a shape by id, and on a superseded result that id belongs to
   * a run that is over.
   *
   * A contour is the third rule and the newest: it is connected and drawn, and
   * it still cannot be corrected — the walls carry the model's reading of the
   * form, not positions somebody may push around, and the backend refuses the
   * edit by name (`contour_not_editable`, contour spec §7). Recognised by the
   * one thing that is true of a contour and of nothing else on this screen:
   * every wall belongs to the run the backend calls `facade`.
   */
  const draggable =
    onEdits !== undefined &&
    chosen !== null &&
    chosen.connected &&
    !isContour(chosen) &&
    !busy &&
    superseded === null;

  // "Teilrekonstruktion" while any wall of the drawn shape still lacks a
  // reading of its own (`isPartial`) — the same condition that keeps a
  // dimension arrow off that wall, spelled out once more so the heading and
  // the drawing never disagree about whether this is the whole house.
  const title =
    chosen !== null && isPartial(chosen) ? "Teilrekonstruktion des Umrisses" : "Umriss";

  return (
    <Panel title={title} hint={paneHint(drawingUrl, drawingPending)}>
      <div className={styles.stack}>
        {picker && confirmedBy === null ? (
          <OutlinePicker
            outlines={outlines}
            onChoose={onChoose}
            busy={busy}
            unavailable={superseded}
          />
        ) : null}

        {/*
          Findings are the reconciliation's verdict on the *chosen* shape, and
          `reconcile` does not run before there is one. That the backend
          reports none until then was the only thing keeping "Abgleich · 17,5
          gelesen, fehlt im Umriss." from appearing above a pane that says
          "Noch keine Form gewählt" (review finding N-6, 10.08.2026). One
          condition, and the screen no longer depends on a promise made
          somewhere else.
        */}
        {chosen && findings.length > 0 ? (
          <ul className={styles.findings}>
            {findings.map((finding, index) => (
              // Keyed by position as well as rule: `_reading_order` and
              // `_landmark_misplaced` each report **one finding per run**, so
              // two separate facts share a rule name whenever more than one
              // Teilzug is out of order — which the backend's corner rework
              // made the normal case, not the exception (review finding I5,
              // 10.08.2026). React kept only a warning for it; the list never
              // reorders, so the index is a stable identity here.
              <li key={`${finding.rule}:${index}`} className={styles.finding} data-rule={finding.rule}>
                <StatusBadge status="review">Abgleich</StatusBadge>
                <span>{finding.message}</span>
              </li>
            ))}
          </ul>
        ) : null}

        <div
          className={`${styles.panes} ${drawingUrl || drawingPending ? "" : styles.panesAlone}`}
        >
          {/*
            No url at all is a different statement from a url that fails: the
            outline was drawn by hand and there never was a sheet (design
            2026-08-15 §4.3). "Original nicht verfügbar" would claim a drawing
            exists and did not load, so the pane is not rendered and the
            reconstruction takes the board.

            Otherwise, keyed by the drawing: `SketchPane` holds "the original
            did not load" in its own state, and nothing resets it. That state
            used to die with the component on the next run; since the result
            survives a run, the component does too, and one 404 said "Original
            nicht verfügbar" for the rest of the session — including after a
            live run that does bring the page (review finding N-4, 10.08.2026).
          */}
          {drawingPending ? (
            <div className={styles.sketchMissing} data-role="sketch-pending">
              <StateMessage title="Original wird geladen …">
                Die Zeichnung dieses Vorgangs wird geholt.
              </StateMessage>
            </div>
          ) : drawingUrl ? (
            <SketchPane key={drawingUrl} url={drawingUrl} />
          ) : null}

          <figure className={styles.model} ref={ref}>
            {chosen && chosen.connected ? (
              <Reconstruction
                figuresAre={figuresAre}
                // Case or shape, either one, is a new drawing — and a view
                // turned for the last one says nothing about this one
                // (binding decision 5, 12.08.2026). Remounting through the
                // key resets `Reconstruction`'s own `view` state to the
                // delivered orientation the same way `SketchPane`'s own key
                // above already resets its "original missing" state.
                key={`${drawingUrl}:${chosen.id}`}
                outline={chosen}
                board={board}
                edits={edits}
                onEdits={draggable ? onEdits : undefined}
              />
            ) : (
              <p className={styles.noShape}>
                {chosen
                  ? "Getrennte Läufe — die Teilzüge bleiben, wie sie gelesen wurden. Eine gemeinsame Form wird dann nicht behauptet und nicht gezeichnet."
                  : "Noch keine Form gewählt. Oben eine Anordnung anklicken — sie wird hier maßstabsgetreu gezeichnet."}
              </p>
            )}
            <figcaption className="label">
              Rekonstruktion
              {chosen && chosen.connected ? (
                <>
                  {" · "}
                  <span className={`num ${chosen.closed ? "" : styles.gapText}`}>
                    {closureText(chosen)}
                  </span>
                </>
              ) : null}
            </figcaption>
          </figure>
        </div>

        <Adjustments
          adjustments={adjustments}
          clamped={editsClamped}
          onReset={onEdits && !busy && superseded === null ? () => onEdits([]) : undefined}
        />

        {confirmedBy !== null ? (
          <div className={styles.confirmedRow}>
            {/* A name only where one was actually given. Until authentication
                exists (WP-E3-x) the reviewer is empty, and printing an
                invented name would fabricate the very provenance this row is
                for — worse than saying nothing (A-014's spirit: never claim
                what did not happen). */}
            <p className={styles.confirmed}>
              {confirmedBy.trim() === "" ? "Bestätigt." : `Bestätigt von ${confirmedBy}.`}
            </p>
            {onContinue ? (
              <Button variant="primary" onClick={onContinue}>
                {continueLabel ?? "Weiter"}
              </Button>
            ) : null}
            <Button onClick={onRevoke}>Bestätigung zurücknehmen</Button>
          </div>
        ) : chosen === null ? (
          // No button and no verdict: there is nothing to confirm and no
          // reconciliation has run. Says what is missing, not what went wrong.
          <p className={styles.awaiting}>
            Zum Bestätigen fehlt die Formauswahl. Oben steht, was sich aus den Teilzügen rechnen
            lässt — die Reihenfolge dort ist Schließungsgüte, keine Empfehlung.
          </p>
        ) : (
          <div className={styles.confirm}>
            <Button
              variant="primary"
              busy={busy}
              disabled={busy || superseded !== null}
              onClick={() => onConfirm(chosen.id)}
            >
              Form bestätigen
            </Button>
            <p className={styles.confirmNote}>
              {superseded === null ? confirmNote(chosen, findings, figuresAre) : SUPERSEDED_CONFIRM_NOTE}
            </p>
          </div>
        )}
      </div>
    </Panel>
  );
}

/**
 * What the machine may claim about the shape somebody is about to confirm.
 *
 * Three different sentences, because three different things happened (review
 * finding C1, 10.08.2026 — one sentence used to claim "nothing is missing and
 * nothing is swapped" in all three, including for the separate arrangement,
 * where `reconcile` does not run at all).
 */
function confirmNote(
  chosen: DemoOutline,
  findings: DemoFinding[],
  figuresAre: FiguresOrigin,
): string {
  if (figuresAre === "set") {
    // A fourth thing that happened: nothing was read, so there was nothing to
    // reconcile a reading against. What the checks did run on is the geometry
    // — the closure — and saying „der Abgleich" here would name a comparison
    // this path never makes.
    return findings.length > 0
      ? "Die Befunde oben kommen aus der Geometrie deiner eigenen Maße — abgeglichen wurde nichts, weil nichts gelesen wurde. Die Entscheidung liegt bei dir."
      : "Deine Maße widersprechen sich nicht. Ob das Haus so aussieht, sagt das nicht — geprüft ist die Geometrie, nicht die Wirklichkeit.";
  }
  if (!chosen.connected) {
    return "Bestätigt wird, dass die Teilzüge getrennt bleiben. Abgeglichen ist dabei nichts — ohne behauptete Form gibt es keine Reihenfolge und keine Vollständigkeit zu prüfen.";
  }
  if (findings.length > 0) {
    return "Der Abgleich hat die Befunde oben. Er kann ebenso danebenliegen wie die Form, deshalb ist das Bestätigen weiter offen — die Entscheidung liegt bei dir.";
  }
  return "Der Abgleich hat nichts Fehlendes und nichts Vertauschtes gefunden. Ob das Haus so aussieht, sagt er nicht.";
}

/**
 * What one corrected wall reads as, in words, from the backend's own figures.
 *
 * Both lengths are the backend's (`AdjustmentOut.lengthBeforeMm` /
 * `lengthAfterMm`) and so is the fact that this wall was touched at all. The
 * only arithmetic is the difference between the two and the sign in front of
 * it — which is formatting, not a measurement: a reader should not have to
 * subtract two four-digit numbers to see that a wall got 600 mm shorter.
 *
 * Deliberately **not** `adjustment.deltaMm`: that figure is how far the wall
 * somebody grabbed *travelled*, and on that wall it sits next to a length
 * that did not change at all (a drag moves a wall perpendicular to itself).
 * The pair of lengths is true for both sorts of wall — the one that was
 * dragged and the neighbours that paid for it.
 */
export function adjustmentText(adjustment: DemoAdjustment): string {
  const difference = adjustment.lengthAfterMm - adjustment.lengthBeforeMm;
  const sign = difference < 0 ? "−" : "+";
  return (
    `manuell angepasst: ${formatMm(adjustment.lengthBeforeMm)} → ` +
    `${formatMm(adjustment.lengthAfterMm)} (${sign}${formatMm(Math.abs(difference))})`
  );
}

/**
 * Every wall a person moved, and the way back.
 *
 * Sits under the two panes rather than on the drawing, because it answers a
 * question the drawing cannot: *what did this wall say before I touched it?*
 * The shape shows the state after; the origin of a figure is the thing this
 * whole screen exists to keep visible (A-014), and a corrected wall whose old
 * length is nowhere on screen has quietly lost it.
 *
 * Nothing is computed here. Which walls a correction touched, how long they
 * were and how long they are now all come from `apply_edits`; where a
 * correction was reined in comes from the same place (`editsClamped`). The
 * pixels a person dragged never reach this list.
 */
function Adjustments({
  adjustments,
  clamped,
  onReset,
}: {
  adjustments: readonly DemoAdjustment[];
  clamped: readonly number[];
  onReset?: (() => void) | undefined;
}) {
  if (adjustments.length === 0 && clamped.length === 0) return null;

  return (
    <div className={styles.adjustments}>
      <div className={styles.adjustmentsHead}>
        <p className={`label ${styles.adjustmentsTitle}`}>
          {adjustments.length === 1 ? "1 Wand von Hand" : `${adjustments.length} Wände von Hand`}
        </p>
        {/* Only where there is something to take back, and only where there
            is somewhere to report it — a reset with no corrections is a
            button that does nothing, and one without `onEdits` is a button
            that cannot. */}
        {adjustments.length > 0 && onReset ? (
          <Button onClick={onReset}>Anpassungen zurücksetzen</Button>
        ) : null}
      </div>

      {adjustments.length > 0 ? (
        <ul className={styles.adjustmentList}>
          {adjustments.map((adjustment) => (
            <li key={adjustment.segmentIndex} className={styles.adjustmentRow}>
              <span className={styles.adjustmentWall}>Wand {adjustment.segmentIndex + 1}</span>
              <span className={`num ${styles.adjustmentFigures}`}>
                {adjustmentText(adjustment)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Once, however many walls hit it: the sentence is about the rule, not
          about a particular wall, and the walls themselves are listed above
          with the length they actually reached. */}
      {clamped.length > 0 ? (
        <p className={styles.clamped}>
          <StatusBadge status="review">Begrenzt</StatusBadge>
          <span>Anpassung an Grenze gestoppt (min. 1 mm Nachbarkante)</span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * The sketch as it went to the model — or, where the process no longer holds
 * it, a sentence saying so.
 *
 * The 404 is documented as reachable in the backend: the demo process keeps
 * only the pages of the case that ran last, and a recorded run brings none.
 * Without this the browser's broken-image glyph sits next to a cleanly drawn
 * reconstruction, in the one pane whose entire purpose is the comparison
 * between the two (review finding I7, 10.08.2026). `DrawingPane` a file over
 * has carried the same state since the Durchstich.
 */
function SketchPane({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className={styles.sketchMissing}>
        <StateMessage title="Original nicht verfügbar">
          Der Demo-Dienst hält nur die Seiten des zuletzt gelaufenen Falls; ein aufgezeichneter Lauf
          bringt die Zeichnung nicht mit. Für den Vergleich muss der Fall live laufen — die
          Rekonstruktion daneben stammt trotzdem aus diesem Lauf.
        </StateMessage>
      </div>
    );
  }

  return (
    <figure className={styles.sketch}>
      <img
        src={url}
        // The page is a sheet (§5.3); its caption follows the mode.
        data-blatt=""
        alt="Skizze, wie sie an das Modell ging"
        onError={() => setFailed(true)}
      />
      <figcaption className="label">Original</figcaption>
    </figure>
  );
}

/**
 * Everything one wall wants written next to it, other than its own figure.
 *
 * The measurement used to be decided here too, as a "dimension" kind text
 * that `placeLabels` could slide along the wall or push out with a leader —
 * which is also what let it drift onto the "?" it was meant to sit beside
 * (review finding, 10.08.2026). It now has a fixed, unambiguous place of its
 * own beside the wall (`dimensionMark`, drawn directly in `Reconstruction`),
 * so this pass keeps only the texts that still need a search for room: the
 * interruption's word and the "?" inside `Wall`, where nothing can know what
 * the neighbouring wall put in the same spot, plus the landmark name a
 * contour wall carries instead of a figure.
 *
 * What each state asks for follows spec §2.6:
 * an **undimensioned** wall asks for nothing of its own (E-14, "keine
 * Maßzahl") — a landmark note is the exception, spelled out below;
 * **interruption** asks for its own word, because it is a gap in the band and
 * not a section; **unclear grating** asks for the "?" mark, the one thing a
 * reader without colour perception has to go on.
 */
function labelRequests(steps: ReturnType<typeof outlineSegments>): LabelRequest[] {
  const requests: LabelRequest[] = [];
  for (const { segment, from, to } of steps) {
    const id = `${segment.runId}-${segment.edgeId}`;
    if (segment.state === "interruption" && segment.interruption) {
      requests.push({ id, kind: "interruption", text: segment.interruption, from, to });
    }
    if (segment.note && segment.state !== "interruption") {
      // Beside the figure, not instead of it. The first version printed a name
      // only where no measurement was, and A-9 then showed none of its four —
      // "Terassentür", "Fallrohr", "Haustür" all sat on measured walls
      // (measured 13.08.2026). That is backwards for what this pane is for: a
      // person matches the drawing to the sheet by its landmarks, and
      // `placeLabels` already knows how to keep two texts off each other.
      requests.push({ id, kind: "landmark", text: segment.note, from, to });
    }
    if (segment.state === "grating_unknown") {
      requests.push({ id, kind: "mark", text: "?", from, to });
    }
  }
  return requests;
}

/** A wall being pushed right now: which one, along which axis, how far in px. */
interface Drag {
  index: number;
  axis: DragAxis;
  /** Where the pointer went down, in client coordinates. */
  fromX: number;
  fromY: number;
  /** How far it has travelled since, along `axis` only. */
  offsetPx: number;
}

/**
 * Everything one wall needs to be grabbed, in one object.
 *
 * Built in `Reconstruction`, where the drag state lives, and handed to `Wall`,
 * which only paints. That split is the same one the module header already
 * describes for the states: a wall does not decide what it is, and it does
 * not decide what a gesture on it means either.
 */
interface WallHandle {
  index: number;
  total: number;
  axis: DragAxis;
  /** Pixels this wall is displaced by *right now* — 0 unless it is the one moving. */
  offsetPx: number;
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
  onPointerCancel: () => void;
  onKeyDown: (event: ReactKeyboardEvent) => void;
}

function Reconstruction({
  figuresAre,
  outline,
  board,
  edits,
  onEdits,
}: {
  /** Whether this outline's figures were read off a sheet or typed by hand. */
  figuresAre: FiguresOrigin;
  outline: DemoOutline;
  board: ProjectionOptions;
  edits: readonly EdgeMoveInput[];
  /** Absent where this drawing is read-only — then no wall gets a grab band. */
  onEdits?: ((edits: EdgeMoveInput[]) => void) | undefined;
}) {
  /**
   * How this drawing is currently turned — a person's choice, made and
   * forgotten here (binding decision 5, 12.08.2026): never persisted, never
   * sent, and reset by `key`ing this component off the case and the chosen
   * shape at the call site below, so a rotation picked for one house is not
   * still standing when the next one is drawn.
   */
  const [view, setView] = useState<ViewState>(VIEW_IDENTITY);

  const { projection, drawn, viewDrawn, requests, dimensions, layout } = useMemo(() => {
    const projected = outlineProjection(outline, board);
    const steps = outlineSegments(outline, board);
    // The pivot for the view transform — fixed to the board's own centre
    // rather than the shape's centroid, so it does not itself move as a
    // wall is dragged (the centroid does: it is recomputed from `outline`,
    // which a correction changes) and every one of the 8 view states turns
    // around the same point.
    const boardCenter = { x: board.width / 2, y: board.height / 2 };
    // Everything downstream that *draws* — the walls' own lines, the label
    // pass, the dimension arrows, the gap line below — reads these turned
    // points and never the originals. Hit-testing and the mm conversion
    // deliberately do not: `dragAxis`/`toMm` keep reading `drawn` further
    // down, in the frame `apply_edits` itself measures in (decision 3).
    const turned = steps.map((step) => ({
      ...step,
      from: applyView(step.from, view, boardCenter),
      to: applyView(step.to, view, boardCenter),
    }));
    const turnedCentroid = applyView(projected.centroid, view, boardCenter);
    const asked = labelRequests(turned);
    // One arrow per wall that was actually read — `null` for the rest, so
    // the render below has one list to walk rather than filtering twice.
    const marks = turned.map((step) => ({
      id: `${step.segment.runId}-${step.segment.edgeId}`,
      segment: step.segment,
      mark: dimensionMark(
        step.segment,
        step.from,
        step.to,
        outwardOffsetPx(step.from, step.to, turnedCentroid, DIMENSION_OFFSET_PX),
      ),
    }));
    // The figures claim their space before any label looks for room. They are
    // drawn outside `placeLabels` (fixed place beside the wall, no
    // alternative), so without this a wall that carries both a reading and a
    // landmark printed the two on top of each other — common on a hand-drawn
    // outline, where naming a wall and measuring it are the same two clicks
    // (measured in the browser, 15.08.2026).
    const reserved = marks
      .filter((entry) => entry.mark !== null && entry.segment.lengthMm !== null)
      .map((entry) =>
        labelBox(
          "dimension",
          formatMm(entry.segment.lengthMm),
          entry.mark!.labelX,
          entry.mark!.labelY,
          entry.mark!.rotate,
        ),
      );
    return {
      projection: projected,
      drawn: steps,
      viewDrawn: turned,
      requests: asked,
      dimensions: marks,
      layout: placeLabels(asked, turnedCentroid, board, reserved),
    };
  }, [outline, board, view]);

  // Only used for its presence: null exactly when there is no non-zero
  // segment left to draw as a dashed line (`gapMm === 0`). The word for the
  // caption below comes from `closureText`, which reads `outline.closed`
  // instead — the two agree except on a folded-back stub, where there is
  // nothing to draw but the shape still is not a closed ring (accepted
  // residual risk, `docs/superpowers/specs/2026-08-09-rostschema-zeichnung-design.md` §6).
  const gap = closureGap(outline);
  // Same non-null assertion `outlineSegments` makes internally: `points()`
  // builds exactly one entry per index of `outline.nodes`, so both ids exist.
  // Turned like every other drawn point above — the gap line is geometry,
  // not a label, and has no legibility concern to keep it out of the turn.
  const boardCenter = { x: board.width / 2, y: board.height / 2 };
  const first = applyView(projection.points["o-n0"]!, view, boardCenter);
  const last = applyView(projection.points[`o-n${outline.nodes.length - 1}`]!, view, boardCenter);
  // A contour never carries grating (module header of `outline.ts`'s
  // `EdgeState`), so neither the wall's own line nor the legend below may
  // still answer that question for one (spec §7) — both switch from
  // `segment.state` to `lengthSource`/`lengthMm` once this is true.
  const contour = isContour(outline);
  const present = STATE_ORDER.filter((state) => drawn.some((step) => step.segment.state === state));
  // The third legend row (`OPEN_END_LEGEND_TEXT`) is not a `segment.state`,
  // so it needs its own gate rather than riding along inside `present`.
  const hasOpenEnd = drawn.some((step) => step.segment.openEnd === true);
  // In request order, which is drawing order — the reader walks the facade in
  // the same sequence below the board as on it.
  const crowded = layout.crowded
    .map((index) => requests[index])
    .filter((request): request is LabelRequest => request !== undefined);

  const [drag, setDrag] = useState<Drag | null>(null);

  /**
   * Give up the gesture without reporting anything.
   *
   * Wrapped for `useEffect` below, which has to hold the identical function
   * across renders or it would tear the Escape listener down and build it up
   * again on every pixel of movement.
   */
  const abandon = useCallback(() => setDrag(null), []);

  /**
   * Escape abandons the drag, wherever the keyboard focus happens to be.
   *
   * On `window`, not on the wall: pressing a pointer down on an SVG element
   * does not focus it in every browser, so a listener on the wall would be a
   * key nobody could press. The listener exists only while something is
   * actually being dragged.
   *
   * Keyed on *whether* something is being dragged, not on the drag itself —
   * the drag object is new on every pixel of travel, and a dependency on it
   * would tear this listener down and build it up again sixty times a second
   * for a fact that changes twice.
   */
  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") abandon();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dragging, abandon]);

  /**
   * A correction, appended to the ones already made and handed upwards.
   *
   * The whole list every time (spec §6.2), never a delta: this process holds
   * no session, so "the corrections" is whatever the next request carries.
   * Nothing here recomputes a length — the request says *which wall* and *how
   * far*, and the binding geometry arrives with the answer.
   */
  const report = useCallback(
    (index: number, deltaMm: number) => {
      if (!onEdits || deltaMm === 0) return;
      onEdits([...edits, { segmentIndex: index, deltaMm }]);
    },
    [onEdits, edits],
  );

  /**
   * Pixels of pointer travel turned into the millimetres of a request.
   *
   * `projection.scale` is the screen pixels one millimetre occupies, fitted
   * once over this outline's own nodes — the same number the drawing itself
   * is built from, so there is no second scale here to drift from it. The
   * rounding is what makes the figure a request rather than a measurement
   * (see `EDIT_STEP_MM`).
   */
  const toMm = useCallback(
    (offsetPx: number) =>
      Math.round(offsetPx / projection.scale / EDIT_STEP_MM) * EDIT_STEP_MM,
    [projection.scale],
  );

  // Computed once per outline, because the answer for one wall depends on its
  // neighbours: asking per wall inside the map would recompute both their
  // axes for every wall on the board.
  const axes = drawn.map(({ from, to }) => dragAxis(from, to));
  const movable = movableWalls(axes, outline.closed);

  function handleFor(index: number, from: ScreenPoint, to: ScreenPoint): WallHandle {
    const axis = dragAxis(from, to);
    const moving = drag?.index === index;

    return {
      index,
      total: drawn.length,
      axis,
      offsetPx: moving ? drag.offsetPx : 0,
      onPointerDown: (event) => {
        // Capture so the wall keeps receiving moves once the pointer leaves
        // its 14 px band — which it does immediately, because leaving the
        // band *is* the gesture.
        event.currentTarget.setPointerCapture(event.pointerId);
        setDrag({ index, axis, fromX: event.clientX, fromY: event.clientY, offsetPx: 0 });
      },
      onPointerMove: (event) => {
        // Read out of the event before the updater, which React is free to
        // run later than this handler and more than once.
        const { clientX, clientY } = event;
        setDrag((current) => {
          if (!current || current.index !== index) return current;
          // The pointer's raw travel is in screen space, but `axis` names a
          // direction in the *local* space this wall's `from`/`to` (above,
          // unturned) are drawn in — the two agree only at the delivered
          // orientation. `invertViewVector` carries the travel back through
          // whatever the view currently does before it is read along an
          // axis, so a 90° turn changes what the gesture looks like without
          // changing what it means to `apply_edits` (decision 3).
          const local = invertViewVector(
            { x: clientX - current.fromX, y: clientY - current.fromY },
            view,
          );
          // One axis only. The travel along the wall is not part of the move:
          // a wall goes perpendicular to itself, and a hand that drifts
          // sideways has not asked for anything (spec §6.1) — true in local
          // space exactly as it was in screen space, because a rotation or a
          // mirror carries perpendicular directions to perpendicular
          // directions; nothing here has to trust that separately.
          const offsetPx = current.axis === "y" ? local.y : local.x;
          return { ...current, offsetPx };
        });
      },
      onPointerUp: () => {
        // No `releasePointerCapture`: the browser drops implicit capture of
        // its own accord once the pointer is up, and asking for a release
        // that already happened is the sort of call that throws.
        if (drag?.index !== index) return;
        setDrag(null);
        report(index, toMm(drag.offsetPx));
      },
      onPointerCancel: abandon,
      onKeyDown: (event) => {
        const step = arrowStep(event.key, axis);
        if (step === null) return;
        // The page must not scroll under a wall somebody is nudging.
        event.preventDefault();
        report(index, step);
      },
    };
  }

  /** Walls whose figures the drag in flight is about to change. */
  const provisional = new Set<string>();
  if (drag) {
    for (const index of [drag.index - 1, drag.index, drag.index + 1]) {
      const step = drawn[index];
      if (step) provisional.add(`${step.segment.runId}-${step.segment.edgeId}`);
    }
  }

  return (
    <>
      <ViewControls view={view} onChange={setView} />

      {/* The drawing is a sheet, paper in the dark too (design 2026-10-24
          §5.3) — a drawing of ours, on warm paper (B-20); the view buttons
          above it follow the mode (§8.3). */}
      <svg
        className={styles.svg}
        data-blatt="zeichnung"
        viewBox={`0 0 ${board.width} ${board.height}`}
        role="img"
        aria-label={`Rekonstruktion, maßstabsgetreu, ${closureText(outline)}`}
        data-view={viewDataAttribute(view)}
      >
        {drawn.map(({ segment, from, to }, index) => {
          // `from`/`to` here stay in local space for `handleFor`, which
          // needs the axis `apply_edits` itself would compute (decision 3);
          // `viewDrawn`'s pair is what actually gets drawn. Same index, same
          // wall — `viewDrawn` is `drawn` mapped through `applyView` one for
          // one, never filtered or reordered.
          const onScreen = viewDrawn[index]!;
          // Which end is the free one, if either — the backend sets
          // `openEnd` on the first segment for `open_at_start` and on the
          // last for `open_at_end` (`_outline_out`), never in the middle, so
          // position alone says which endpoint of *this* wall it names. Both
          // ends on a one-wall outline, where the first segment is also the
          // last.
          const openEndSide: "from" | "to" | "both" | null = !segment.openEnd
            ? null
            : index === 0 && index === drawn.length - 1
              ? "both"
              : index === 0
                ? "from"
                : "to";
          return (
            <Wall
              key={`${segment.runId}-${segment.edgeId}`}
              segment={segment}
              from={onScreen.from}
              to={onScreen.to}
              view={view}
              contour={contour}
              openEndSide={openEndSide}
              handle={onEdits && movable.has(index) ? handleFor(index, from, to) : undefined}
              provisionalMm={drag?.index === index ? toMm(drag.offsetPx) : null}
            />
          );
        })}
        {/* The dimension arrow beside each read wall (E-13) — fixed beside
            its own wall, never searched for room like the labels below, so
            it always spans exactly the stretch it measures. `mark` is `null`
            wherever `dimensionMark` found no reading to show (`lengthMm` or
            `lengthSource` says so), which is also the only time
            `segment.lengthMm` could be `null` here — the `!` below reads
            that guarantee back rather than re-checking it.

            Dimmed under the same `provisional` set `WallLabel` reads below:
            a drag in flight is about to change this very figure (the wall
            moving and the neighbours absorbing it), and the number on
            screen is still the one the backend last confirmed — it stays
            legible while it is being argued with, exactly as a landmark or
            an interruption's word already does (A-014). */}
        {dimensions.map(({ id, segment, mark }) =>
          mark ? (
            <g
              key={`dim-${id}`}
              className={
                provisional.has(id) ? `${styles.dimensionMark} ${styles.provisional}` : styles.dimensionMark
              }
              data-role="dimension-mark"
              data-edge={id}
              data-provisional={provisional.has(id) ? "true" : undefined}
            >
              <path className={styles.dimension} d={`${mark.line} ${mark.ticks}`} />
              <text
                x={mark.labelX}
                y={mark.labelY}
                transform={`rotate(${mark.rotate} ${mark.labelX} ${mark.labelY})`}
                className={styles.dimensionLabel}
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {formatMm(segment.lengthMm!)}
              </text>
            </g>
          ) : null,
        )}
        {/* All text above all lines, so no label is overdrawn by the hatch of
            a wall that happens to be painted later. */}
        {layout.placed.map((placement) => (
          <WallLabel
            key={`label-${placement.index}`}
            placement={placement}
            provisional={provisional.has(placement.id)}
          />
        ))}
        {gap ? (
          <line
            className={styles.gap}
            data-role="closure-gap"
            x1={last.x}
            y1={last.y}
            x2={first.x}
            y2={first.y}
            stroke="var(--st-conflict)"
            strokeWidth={2}
          />
        ) : null}
      </svg>

      {crowded.length > 0 ? <CrowdedLabels requests={crowded} /> : null}

      {contour && present.length > 0 ? (
        <ul className={styles.legend}>
          {contourLegend(figuresAre).map(({ state, text }) => (
            <li key={state} className={styles.legendItem} data-legend={state}>
              <LegendSwatch state={state} />
              {text}
            </li>
          ))}
          {hasOpenEnd ? (
            <li className={styles.legendItem} data-legend="open-end">
              <OpenEndLegendSwatch />
              {OPEN_END_LEGEND_TEXT}
            </li>
          ) : null}
        </ul>
      ) : !contour && present.length > 0 ? (
        <ul className={styles.legend}>
          {present.map((state) => (
            // `data-legend`, not `data-state`: the walls in the drawing above
            // already carry `data-state`, and a query for one of them would
            // otherwise find its legend entry as well — a test counting walls
            // would count words too (review finding, 10.08.2026, on tests that
            // pass for the wrong reason).
            <li key={state} className={styles.legendItem} data-legend={state}>
              <LegendSwatch state={state} />
              {STATE_LABEL[state]}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/**
 * The four small turns/mirrors a person can apply to the drawing, plus the
 * one-step way back once any of them has done something (binding decision
 * 2, 12.08.2026). Rendered only where `Reconstruction` itself is — before a
 * shape is chosen, or on the unconnected arrangement, there is no drawing
 * for a button here to turn, and none is offered.
 *
 * Purely local: `onChange` only ever replaces the `ViewState` `Reconstruction`
 * holds, never anything sent to the backend (binding decision 1) — a rotated
 * view changes no number on screen and no later request.
 *
 * German labels double as `aria-label`s rather than icons — this file's own
 * convention throughout (the legend, the wall states) is that a printed word
 * beats a glyph a colour-blind or screen-reader user might not get, and a
 * rotation icon that reads badly enough to need mirroring is not the icon to
 * bet this control on.
 */
function ViewControls({
  view,
  onChange,
}: {
  view: ViewState;
  onChange: (view: ViewState) => void;
}) {
  return (
    <div className={styles.viewControls} data-role="view-controls">
      <Button variant="quiet" aria-label="90° nach links" onClick={() => onChange(rotateViewLeft(view))}>
        90° nach links
      </Button>
      <Button
        variant="quiet"
        aria-label="90° nach rechts"
        onClick={() => onChange(rotateViewRight(view))}
      >
        90° nach rechts
      </Button>
      <Button
        variant="quiet"
        aria-label="waagerecht spiegeln"
        onClick={() => onChange(flipViewHorizontal(view))}
      >
        waagerecht spiegeln
      </Button>
      <Button
        variant="quiet"
        aria-label="senkrecht spiegeln"
        onClick={() => onChange(flipViewVertical(view))}
      >
        senkrecht spiegeln
      </Button>
      {/* Only once there is something to undo — the same rule the
          adjustments' own reset a few components over already follows: a
          control that does nothing invites a click looking for whatever it
          was supposed to do. */}
      {!isViewIdentity(view) ? (
        <Button variant="quiet" onClick={() => onChange(VIEW_IDENTITY)}>
          Ansicht zurücksetzen
        </Button>
      ) : null}
    </div>
  );
}

/**
 * One piece of text on the drawing, at the spot `placeLabels` found for it.
 *
 * All three sorts are drawn here and none of them anywhere else. That is the
 * point: as long as `Wall` set the interruption's word and the "?" itself,
 * the only pass that could see a collision saw one text in three (review
 * finding, 10.08.2026 — measured in the browser, two overlapping pairs and a
 * name 21 px past the edge). A fourth sort has to come through
 * `labelRequests` and `LABEL_STYLE`, or it does not appear.
 *
 * Measurements are set with `formatMm` like everywhere else — the normalised
 * millimetre with its unit, the same figure the Werteliste leads with. The
 * verbatim reading ("17,5") stays in that list; printing both here would
 * double the ink on the one pane whose job is the comparison of shapes.
 *
 * `data-edge` ties the label back to its wall, so a test can ask whether the
 * mark sits on the unclear wall rather than merely whether one exists.
 */
function WallLabel({
  placement,
  provisional = false,
}: {
  placement: LabelPlacement;
  /**
   * True while a drag in flight is about to change this figure — the wall
   * being moved and the two that will have to absorb it.
   *
   * Dimmed rather than hidden or updated: the figure on screen is still the
   * one the backend last said, and it stays readable while it is being argued
   * with. Recomputing it here would be the display layer answering a question
   * only `apply_edits` may answer.
   */
  provisional?: boolean;
}) {
  const { x, y, rotation, leader, kind } = placement;
  const style = LABEL_STYLE[kind];

  return (
    <g
      className={provisional ? `${styles.wallLabel} ${styles.provisional}` : styles.wallLabel}
      data-role="wall-label"
      data-kind={kind}
      data-edge={placement.id}
      data-tier={placement.tier}
      data-provisional={provisional ? "true" : undefined}
    >
      {leader ? (
        <line
          className={styles.figureLeader}
          x1={leader.x1}
          y1={leader.y1}
          x2={leader.x2}
          y2={leader.y2}
        />
      ) : null}
      <text
        x={x}
        y={y}
        transform={rotation ? `rotate(${rotation} ${x} ${y})` : undefined}
        className={style.className}
        fill={style.fill}
        textAnchor="middle"
        dominantBaseline="middle"
        // The mark repeats what the legend spells out in words; a screen
        // reader that read every "?" would hear punctuation, not a state.
        aria-hidden={kind === "mark" ? true : undefined}
      >
        {placement.text}
      </text>
    </g>
  );
}

/**
 * What the head above the crowded list says, in words rather than a bare
 * count.
 *
 * Used to cover two sorts of news at once — a measurement nobody could read
 * on the drawing, and a doorway nobody could see named — because the
 * dimension figure used to be searched for room like everything else here.
 * It no longer is: `dimensionMark` sits at a fixed offset beside its own
 * wall and cannot run out of room to report (Task 10), so only the
 * interruption's word can still land in this list, and this now says only
 * that. The two-sort version stayed correct after that change and was still
 * tested directly for a scenario `CrowdedLabels` could no longer produce —
 * dead coverage rather than a caught regression (fix round 1, review
 * finding).
 */
export function crowdedHeading(names: number): string {
  return `${names} ${names === 1 ? "Unterbrechung" : "Unterbrechungen"} ohne Platz in der Zeichnung`;
}

/**
 * The interruption names the drawing had no room for.
 *
 * Some facades cannot carry all their labels at any board size: A-9's first
 * run has fourteen sections between 25 mm and 1.300 mm, four of them under
 * 100 mm in a row, and at the scale that fits the house those four are three
 * pixels apart while their figures are thirty pixels wide. That is geometry,
 * not a defect.
 *
 * What must not happen is that a name disappears. A missing name reads as a
 * facade with no doorway in it — the misreading this screen exists to
 * prevent. So it is printed, in drawing order, under the board it did not
 * fit on.
 */
function CrowdedLabels({ requests }: { requests: LabelRequest[] }) {
  // Interruptions only, now the one kind of text this list can ever hold: a
  // mark never reaches it (placed unconditionally on its own wall), and the
  // dimension figure moved out of the searched-for-room system entirely
  // (Task 10, fix round 1) — `LABEL_STYLE`'s own "dimension" entry and
  // `LabelKind` itself still name it, only because `lib/labels.ts` is
  // generic over all four kinds and is not this task's file to narrow.
  const names = requests.filter((request) => request.kind === "interruption");
  // Defensive, not merely tidy: a landmark note is also movable and can in
  // principle land in `requests` uncounted by the filter above (a
  // pre-existing gap, not one this fix introduces or closes) — without this,
  // that case would render an empty box with a "0 Unterbrechungen" heading
  // instead of nothing.
  if (names.length === 0) return null;

  return (
    <div className={styles.crowded}>
      <p className={`label ${styles.crowdedHead}`}>{crowdedHeading(names.length)}</p>
      <p className={styles.crowdedNames}>{names.map((request) => request.text).join("  ·  ")}</p>
      <p className={styles.crowdedNote}>
        In Laufrichtung. Die Abschnitte sind gezeichnet, sie stehen nur zu dicht beieinander für
        eine eigene Beschriftung.
      </p>
    </div>
  );
}

/**
 * The legend entry drawn the way the wall itself is drawn.
 *
 * A coloured bar was not enough (review finding I9, 10.08.2026): `Rost` and
 * `Rost unklar` differed by hue alone at 1,12:1, and `kein Rost` and
 * `kein Maß` were both `--ink-3` and therefore pixel-identical. Somebody who
 * spots the "?" on a wall found nothing in the legend connecting them. Each
 * swatch now runs through the same `LINE_STYLE` table the drawing uses, so
 * hatch, hairline, dash and the question mark appear in both places and the
 * legend stays legible in greyscale.
 */
function LegendSwatch({ state }: { state: EdgeState }) {
  const colour = STATE_COLOUR[state];
  const from = { x: 1, y: 10 };
  const to = { x: 33, y: 10 };

  if (state === "interruption") {
    // Two stubs with a gap between them — spec §2.6's "Lücke im Band", which
    // is exactly what the drawing shows: no line, the interruption's word.
    return (
      <svg className={styles.legendSwatch} viewBox="0 0 34 14" aria-hidden="true">
        <line x1={1} y1={10} x2={11} y2={10} stroke={colour} strokeWidth={2} />
        <line x1={23} y1={10} x2={33} y2={10} stroke={colour} strokeWidth={2} />
      </svg>
    );
  }

  const style = LINE_STYLE[state];

  return (
    <svg className={styles.legendSwatch} viewBox="0 0 34 14" aria-hidden="true">
      <line
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        stroke={colour}
        strokeWidth={style.width}
        strokeLinecap={style.linecap}
        strokeDasharray={style.dash}
      />
      {style.hatch
        ? gratingHatch(from, to, HATCH_SPACING, 3).map((tick, index) => (
            <line
              key={index}
              x1={tick.x1}
              y1={tick.y1}
              x2={tick.x2}
              y2={tick.y2}
              stroke={colour}
              strokeWidth={1}
              opacity={0.6}
            />
          ))
        : null}
      {state === "grating_unknown" ? (
        <text x={17} y={4} className={styles.legendMark} fill={colour} textAnchor="middle">
          ?
        </text>
      ) : null}
    </svg>
  );
}

/**
 * The legend entry for "auslaufend gestrichelt" (spec §7) — the one state
 * `LegendSwatch` above cannot draw, because it is not a `segment.state` at
 * all. It is the same wall's own dash pattern changing partway along its
 * length (`fadeAtOpenEnd` inside `Wall`), so this mirrors that drawing
 * directly: solid for the first stretch, `undimensioned`'s own dash at half
 * opacity for the rest — a bare line, never `form_only`'s or
 * `undimensioned`'s colour alone, because an open end can land on a read
 * wall exactly as it can land on an unread one (fix round 1).
 */
function OpenEndLegendSwatch() {
  const colour = STATE_COLOUR.form_only;
  const split = 20;

  return (
    <svg className={styles.legendSwatch} viewBox="0 0 34 14" aria-hidden="true">
      <line
        x1={1}
        y1={10}
        x2={split}
        y2={10}
        stroke={colour}
        strokeWidth={LINE_STYLE.form_only.width}
        strokeLinecap={LINE_STYLE.form_only.linecap}
      />
      <line
        className={styles.openEndFade}
        x1={split}
        y1={10}
        x2={33}
        y2={10}
        stroke={colour}
        strokeWidth={LINE_STYLE.undimensioned.width}
        strokeLinecap={LINE_STYLE.undimensioned.linecap}
        strokeDasharray={LINE_STYLE.undimensioned.dash}
      />
    </svg>
  );
}

/** The point a fraction `t` of the way from `a` to `b`. */
function lerpPoint(a: ScreenPoint, b: ScreenPoint, t: number): ScreenPoint {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/** How much of a wall's own length the fade at an open end covers. Short
 * enough that the wall still reads as the length it is; long enough that
 * "runs out" is visible rather than a single pixel of difference. */
const OPEN_END_FADE_FRACTION = 0.2;

/**
 * A wall's line, split at whichever end(s) run out — the part that still
 * reads at full weight, and zero, one or two short tails at half opacity for
 * `openEndSide` (spec: "the open end runs out").
 *
 * A separate segment rather than one line with a gradient: this app draws
 * every other partial stroke (hatch, dash) as discrete `<line>`s already, and
 * two more cost nothing a `<linearGradient>` in `<defs>` would not — one id
 * to keep unique per wall, one indirection to read.
 */
function fadeAtOpenEnd(
  from: ScreenPoint,
  to: ScreenPoint,
  side: "from" | "to" | "both" | null,
): { base: { from: ScreenPoint; to: ScreenPoint }; fades: { from: ScreenPoint; to: ScreenPoint }[] } {
  if (side === null) return { base: { from, to }, fades: [] };
  const nearFrom = lerpPoint(from, to, OPEN_END_FADE_FRACTION);
  const nearTo = lerpPoint(from, to, 1 - OPEN_END_FADE_FRACTION);
  if (side === "both") {
    return { base: { from: nearFrom, to: nearTo }, fades: [{ from, to: nearFrom }, { from: nearTo, to }] };
  }
  if (side === "from") return { base: { from: nearFrom, to }, fades: [{ from, to: nearFrom }] };
  return { base: { from, to: nearTo }, fades: [{ from: nearTo, to }] };
}

/**
 * One drawn wall. Coloured and patterned by `segment.state` for an
 * arrangement of partial runs — `demo.py`'s `_segment_state` already ranked
 * interruption over "no dimension" over the grating question, and this only
 * paints what it is handed (module header: "im Frontend wird nicht
 * abgeleitet, nur gemalt"). A contour asks a different question: it never
 * carries grating (module header of `outline.ts`'s `EdgeState`), so `contour`
 * switches the choice from `segment.state` to whether this wall was actually
 * read (`lengthMm`/`lengthSource`) — solid where it was, dashed where it was
 * not (spec §7).
 *
 * **Lines and hatch only.** The "?" for unconfirmed grating (spec §2.6's
 * "Fragezeichen-Marke") and the interruption's own word used to be set here,
 * each at the midpoint of its own wall, each blind to what the wall next to
 * it had put in the same place. That is how the mark came to sit on the
 * measurement it was meant to qualify, and how a 130 px name ran 21 px off
 * the board (review finding, 10.08.2026, measured in the browser). Both are
 * now `WallLabel`s, placed by the one pass that can see all of them —
 * unchanged in what they say, only in where they land.
 *
 * An interruption draws nothing at all: it is a gap in the band, and a gap is
 * drawn by not drawing. Its word arrives with the labels.
 */
function Wall({
  segment,
  from,
  to,
  view,
  contour,
  openEndSide = null,
  handle,
  provisionalMm = null,
}: {
  segment: DemoSegment;
  from: ScreenPoint;
  to: ScreenPoint;
  /** How this wall is currently turned — `from`/`to` above are already
   * drawn through it; this is only for the *live* preview below, which has
   * to carry a fresh pointer offset through the same turn on every frame. */
  view: ViewState;
  /** Whether this wall belongs to a facade contour — see the function
   * comment above for what that changes. */
  contour: boolean;
  /** Which end of this wall the drawing was cut short at, if either
   * (`segment.openEnd`, resolved to a concrete end by `Reconstruction`,
   * which alone knows this wall's position among its outline's own). */
  openEndSide?: "from" | "to" | "both" | null;
  /** Everything needed to grab this wall — absent on a read-only drawing. */
  handle?: WallHandle | undefined;
  /** The millimetres this wall's own drag currently stands at, while it moves. */
  provisionalMm?: number | null;
}) {
  if (segment.state === "interruption") return null;

  // Off a contour, unchanged: the grating vocabulary still answers "what is
  // on this wall?". On one, there is no such answer to give (spec §7), so the
  // same two-way choice `form_only`/`undimensioned` already draws — solid
  // ink-2, dashed ink-3 — is made from the field built for it instead of from
  // `state`, which happens to agree today only because `_segment_state`
  // derives `form_only` from the very same "was this read" question.
  const read = isSegmentRead(segment);
  const colour = contour ? STATE_COLOUR[read ? "form_only" : "undimensioned"] : STATE_COLOUR[segment.state];
  const style = contour ? LINE_STYLE[read ? "form_only" : "undimensioned"] : LINE_STYLE[segment.state];
  const adjusted = segment.origin === "user_adjusted";
  // Pixels only, and only while a hand is on it. The wall is drawn where the
  // backend last put it; this shifts the picture of it so a person can see
  // what they are asking for before they ask (spec §6.3).
  //
  // `handle.offsetPx` is in *local* space (§ onPointerMove above) — the same
  // space this wall's own `from`/`to` were drawn through `applyView` to
  // leave before they ever reached this component. Forward through the same
  // view here turns that local offset back into the screen vector the
  // pointer itself produced, so the preview slides under the pointer rather
  // than off at whatever angle the local axis used to mean.
  const shift = handle?.offsetPx ?? 0;
  const localShift = handle?.axis === "x" ? { x: shift, y: 0 } : { x: 0, y: shift };
  const screenShift = shift === 0 ? localShift : applyViewVector(localShift, view);
  const transform = shift === 0 ? undefined : `translate(${screenShift.x} ${screenShift.y})`;
  // Which screen direction that same vector actually runs in — a rotation
  // maps a pure local axis to a pure screen axis (never a diagonal), so
  // exactly one of the two components is zero and this reads off the other
  // one exactly, not just approximately. Used only to place the readout
  // below; the mm figure it shows came from local space and is untouched.
  const screenAxis: DragAxis = Math.abs(screenShift.x) > Math.abs(screenShift.y) ? "x" : "y";
  const { base, fades } = fadeAtOpenEnd(from, to, openEndSide);

  return (
    <g
      className={adjusted ? styles.adjusted : undefined}
      data-state={segment.state}
      data-origin={adjusted ? "user_adjusted" : undefined}
      data-open-end={openEndSide ?? undefined}
      transform={transform}
    >
      {/* Under the wall, never instead of it: the state colour still says
          whether there are goods on this section, and a correction may not
          overwrite that answer with a different one. A wider band in the
          confirmed colour is a difference in *shape* as well as in hue, so it
          survives greyscale and a red-green weak reader alike (CI §7). */}
      {adjusted ? (
        <line
          className={styles.adjustedBand}
          x1={from.x}
          y1={from.y}
          x2={to.x}
          y2={to.y}
        />
      ) : null}
      <line
        x1={base.from.x}
        y1={base.from.y}
        x2={base.to.x}
        y2={base.to.y}
        stroke={colour}
        strokeWidth={style.width}
        strokeLinecap={style.linecap}
        strokeDasharray={style.dash}
      />
      {/* "auslaufend gestrichelt" — the design's own third state (spec §7),
          distinct from both "durchgezogen" and "gestrichelt": the last
          stretch before a free end, where the drawing breaks off rather than
          the model having seen a corner. Always the dash of `undimensioned`,
          never `style.dash` — a *read*, solid wall whose far end is open
          must not draw a merely-fainter solid line there, which is a fourth,
          unnamed thing this screen would then be showing without a word for
          it (fix round 1: reachable whenever `open_at_start`/`open_at_end`
          lands on a wall that does carry a reading). Half opacity on top of
          the dash, not instead of it. */}
      {fades.map((tail, index) => (
        <line
          key={`fade-${index}`}
          className={styles.openEndFade}
          x1={tail.from.x}
          y1={tail.from.y}
          x2={tail.to.x}
          y2={tail.to.y}
          stroke={colour}
          strokeWidth={style.width}
          strokeLinecap={LINE_STYLE.undimensioned.linecap}
          strokeDasharray={LINE_STYLE.undimensioned.dash}
        />
      ))}
      {style.hatch
        ? gratingHatch(from, to, HATCH_SPACING, HATCH_HALF_WIDTH).map((tick, index) => (
            <line
              key={index}
              x1={tick.x1}
              y1={tick.y1}
              x2={tick.x2}
              y2={tick.y2}
              stroke={colour}
              strokeWidth={1}
              opacity={0.6}
            />
          ))
        : null}
      {handle ? <WallGrip segment={segment} from={from} to={to} handle={handle} /> : null}
      {provisionalMm !== null && provisionalMm !== 0 ? (
        <ProvisionalReadout from={from} to={to} axis={screenAxis} valueMm={provisionalMm} />
      ) : null}
    </g>
  );
}

/**
 * The invisible band a wall is grabbed by, and the wall's own keyboard.
 *
 * A drawn wall is one to two pixels wide and nobody hits that with a mouse,
 * let alone a finger — so the thing that takes the gesture is a 14 px stroke
 * over the same two points, painted in nothing and listening on its stroke
 * (`pointer-events: stroke` ignores the paint; `visiblePainted` would not
 * hear a transparent line at all).
 *
 * It is also the wall's keyboard, which is why it is a `slider` and not a
 * decoration: it takes focus, says how long the wall it belongs to is, and
 * announces the axis its two arrow keys work on. A drag that only a mouse can
 * perform is a correction only a mouse user can make.
 */
function WallGrip({
  segment,
  from,
  to,
  handle,
}: {
  segment: DemoSegment;
  from: ScreenPoint;
  to: ScreenPoint;
  handle: WallHandle;
}) {
  // Same guard `dimensionMark` applies before printing a figure (A-014):
  // `WallGrip` only ever renders on a draggable, non-contour wall today
  // (`!isContour(chosen)`, `Reconstruction`'s own `draggable`), where
  // `lengthMm` and `lengthSource` never disagree in practice — but this is
  // the one place in the component that reads `lengthMm` for something a
  // person actually hears rather than sees, and it must not depend on an
  // invariant enforced two call sites away to stay honest (fix round 1,
  // review finding).
  const read = isSegmentRead(segment);
  const valueText = read ? formatMm(segment.lengthMm!) : "kein Maß";
  const direction = handle.axis === "y" ? "senkrecht" : "waagerecht";

  return (
    <line
      className={`${styles.grip} ${handle.axis === "y" ? styles.gripNs : styles.gripEw}`}
      data-role="wall-handle"
      data-segment={handle.index}
      x1={from.x}
      y1={from.y}
      x2={to.x}
      y2={to.y}
      strokeWidth={GRAB_HALF_WIDTH * 2}
      role="slider"
      tabIndex={0}
      aria-label={`Wand ${handle.index + 1} von ${handle.total}, ${valueText}, ${direction} verschiebbar`}
      // The axis the two arrow keys work on, which is what `aria-orientation`
      // is for — not the direction the wall is drawn in. Up/Down is a
      // vertical slider even where the wall itself runs across the page.
      aria-orientation={handle.axis === "y" ? "vertical" : "horizontal"}
      aria-valuenow={read ? segment.lengthMm! : undefined}
      aria-valuetext={valueText}
      onPointerDown={handle.onPointerDown}
      onPointerMove={handle.onPointerMove}
      onPointerUp={handle.onPointerUp}
      onPointerCancel={handle.onPointerCancel}
      onKeyDown={handle.onKeyDown}
    />
  );
}

/**
 * What the wall under the pointer would ask for, if it were let go now.
 *
 * Sits inside the wall's own group, so it travels with the preview instead of
 * being positioned from client coordinates the SVG cannot see. It carries the
 * word „vorläufig" because that is the whole claim being made: this figure
 * was computed from pixels, and the millimetres that count come back from
 * `apply_edits` (A-014 — a number without its origin is what this screen
 * exists not to show).
 */
function ProvisionalReadout({
  from,
  to,
  axis,
  valueMm,
}: {
  from: ScreenPoint;
  to: ScreenPoint;
  axis: DragAxis;
  valueMm: number;
}) {
  const x = (from.x + to.x) / 2 + (axis === "x" ? Math.sign(valueMm) * 18 : 0);
  const y = (from.y + to.y) / 2 + (axis === "y" ? Math.sign(valueMm) * 18 : 6);

  return (
    <text
      className={styles.readout}
      data-role="drag-readout"
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {signedMm(valueMm)} · vorläufig
    </text>
  );
}
